import { Component, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createGuid } from '../../../core/helpers/utility';
import { TagsApiService } from '../tags.apiservice';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
@Component({
  selector: 'app-edit-tag',
  templateUrl: './edit-tag.component.html',
  styleUrls: ['./edit-tag.component.scss']
})
export class EditTagComponent implements OnInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  editTagForm!: FormGroup;
  submitted = false;
  allCategories: string[] = [];
  addCategoryTag = (value: string): string => {
    const trimmedValue = this.cleanCategoryValue(String(value ?? '').trim());
    if (!trimmedValue.length) {
      return '';
    }

    const existingCategory = this.findExistingCategory(trimmedValue);
    if (existingCategory) {
      return existingCategory;
    }

    this.allCategories = this.mergeCategoryOptions(this.allCategories, [trimmedValue]);
    return trimmedValue;
  };
  @Input() iId: string | null = null;
  @Output() oSaveClicked = new EventEmitter<void>();

  constructor(
    private formBuilder: FormBuilder,
    private tagsApiService: TagsApiService,
    @Optional() public activeModal?: NgbActiveModal) {

    this.editTagForm = this.formBuilder.group({
      id: [null],
      name: ['', [Validators.required]],
      description: ['', Validators.required],
      categories: [[], [Validators.required]],
      clientId: [null],
      campaignId: [null]
    });
  }

  ngOnInit(): void {
    this.tagsApiService.getAllTags(null)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.allCategories = this.extractDistinctCategories(response?.data ?? response);
        this.syncSelectedCategoriesWithOptions();
      });

    const id = this.iId;
    if (id) {
      this.tagsApiService.getTagDetails(id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          const categories = this.normalizeCategories(response.data?.categories);
          this.editTagForm.patchValue({
            ...response.data,
            categories
          });
          this.allCategories = this.mergeCategoryOptions(this.allCategories, categories);
          this.syncSelectedCategoriesWithOptions();
        });
    }

  }
  get f() { return this.editTagForm.controls; }

  onClientRowClick(evt: any) {
    //this.router.navigate(['/pages/tag-details', evt.id]);
  }

  onSubmit() {
    this.submitted = true;
    if (this.editTagForm.invalid)
      return;
    if (this.f.id.value === null)
      this.f.id.setValue(createGuid())

    var request = {
      ...this.editTagForm.value,
      categories: this.normalizeCategories(this.editTagForm.value.categories).join(', ')
    };
    request = this.trimObjectStrings(request);

    this.tagsApiService.saveTag(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.oSaveClicked.emit();
          this.activeModal?.close(true);
        }
      });
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  private normalizeCategories(value: unknown): string[] {
    if (Array.isArray(value)) {
      return this.dedupeCategories(value.map((entry) => this.cleanCategoryValue(String(entry ?? '').trim())));
    }

    if (typeof value === 'string') {
      return this.dedupeCategories(value.split(',').map((entry) => this.cleanCategoryValue(entry.trim())));
    }

    return [];
  }

  private extractDistinctCategories(tags: any[] | null | undefined): string[] {
    if (!tags?.length) {
      return [];
    }

    return this.dedupeCategories(
      tags.flatMap((tag) => this.normalizeCategories(tag?.categories))
    ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
  }

  private mergeCategoryOptions(existing: string[], incoming: string[]): string[] {
    return this.dedupeCategories([...existing, ...incoming])
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
  }

  private syncSelectedCategoriesWithOptions(): void {
    const selectedCategories = this.normalizeCategories(this.f.categories.value);
    const canonicalCategories = this.dedupeCategories(
      selectedCategories.map((category) => this.findExistingCategory(category) ?? category)
    );

    if (selectedCategories.join('|') !== canonicalCategories.join('|')) {
      this.f.categories.setValue(canonicalCategories, { emitEvent: false });
    }
  }

  private dedupeCategories(values: string[]): string[] {
    const categoryMap = new Map<string, string>();

    values.forEach((value) => {
      const cleanedValue = this.cleanCategoryValue(String(value ?? '').trim());
      if (!cleanedValue.length) {
        return;
      }

      const categoryKey = this.getCategoryKey(cleanedValue);
      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, cleanedValue);
      }
    });

    return Array.from(categoryMap.values());
  }

  private findExistingCategory(value: string): string | undefined {
    const categoryKey = this.getCategoryKey(value);
    return this.allCategories.find((category) => this.getCategoryKey(category) === categoryKey);
  }

  private getCategoryKey(value: string): string {
    return String(value ?? '').trim().toLocaleLowerCase();
  }

  private cleanCategoryValue(value: string): string {
    return String(value ?? '').trim().replace(/\s+/g, ' ');
  }

  ngAfterViewInit() {

  }

  onCancel() {
    this.activeModal?.dismiss();
  }

}
